import { Spinner, SuccessCheck } from './feedback';
import { useEffect, useState } from 'react';
import type { Job } from '../api/types';
import { formatDuration, stepLabel } from '../format';

/** 終わった段階を 1、動いている段階を半分として数えた進み具合（%）。成功したジョブは 100 */
export function jobPercent(job: Pick<Job, 'status' | 'steps'>): number {
  if (job.status === 'succeeded' || job.steps.length === 0) return job.status === 'succeeded' ? 100 : 0;
  const done = job.steps.filter((step) => ['succeeded', 'failed', 'cancelled'].includes(step.status)).length;
  const running = job.steps.some((step) => step.status === 'running') ? 0.5 : 0;
  return Math.min(99, Math.round(((done + running) / job.steps.length) * 100));
}

/** 動いているジョブの進み具合と、今の段階・その段階の経過時間を出す。1 秒ごとに経過時間を進める */
export function JobProgress({ job }: { job: Job }) {
  const active = job.status === 'queued' || job.status === 'running';
  const [, tick] = useState(0);
  useEffect(() => {
    if (!active) return;
    const timer = setInterval(() => tick((value) => value + 1), 1000);
    return () => clearInterval(timer);
  }, [active]);
  if (job.status === 'succeeded')
    return (
      <div role="status" className="job-success">
        <SuccessCheck />
        完了しました
      </div>
    );
  if (!active) return null;
  const percent = jobPercent(job);
  const index = job.steps.findIndex((step) => step.status === 'running' || step.status === 'queued');
  const step = index >= 0 ? job.steps[index] : undefined;
  return (
    <div className="meter" role="group" aria-label="ジョブの進み具合">
      <div className="meter-head">
        <span>
          <Spinner />
          {percent}%{step && ` ・ ${index + 1}/${job.steps.length} 段階目: ${stepLabel[step.name] ?? step.name}`}
        </span>
        {step?.startedAt && <span>この段階 {formatDuration(step.startedAt, undefined)}</span>}
      </div>
      <div
        className="meter-track"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percent}
        aria-label={`${percent}%`}
      >
        <div className="meter-fill" style={{ width: `${percent}%` }} />
      </div>
    </div>
  );
}
